import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { 
  Box, 
  Heading, 
  Text, 
  UnorderedList, 
  OrderedList, 
  ListItem, 
  Code, 
  Divider,
  Link,
  Image,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td
} from '@chakra-ui/react';

const MarkdownRenderer = ({ content, ...props }) => {
  // Clean content function to remove trailing HTML elements and entities
  const cleanContent = (rawContent) => {
    if (!rawContent) return '';
    
    let cleaned = rawContent;
    cleaned = cleaned.replace(/\[\]\([^)]+\)/g, '');

    // Keep cleaning until no more changes are made
    let previousLength;
    do {
      previousLength = cleaned.length;
      
      cleaned = cleaned
        // Remove trailing whitespace and newlines
        .trim()
        // Remove trailing &nbsp; entities (multiple passes)
        .replace(/(&nbsp;|\u00A0|\s)+$/g, '')
        // Remove trailing <br> tags (multiple passes)
        .replace(/(<br\s*\/?>)+$/gi, '')
        // Remove trailing empty paragraphs with any content
        .replace(/<p[^>]*>(&nbsp;|\u00A0|\s|<br\s*\/?>)*<\/p>$/gi, '')
        // Remove trailing content from list items before closing tags
        .replace(/(<br\s*\/?>|&nbsp;|\u00A0|\s)+(<\/li>\s*<\/(ul|ol)>\s*)$/gi, '$2')
        // Remove trailing content from list items at the very end
        .replace(/(<br\s*\/?>|&nbsp;|\u00A0|\s)+<\/li>$/gi, '</li>')
        // Remove any remaining trailing entities and tags
        .replace(/(&nbsp;|\u00A0|<br\s*\/?>|\s)+$/gi, '')
        // Final trim
        .trim();
        
    } while (cleaned.length !== previousLength && cleaned.length > 0);
    
    return cleaned;
  };

  // Custom components for rendering markdown elements
  const components = {
    // Headings
    h1: ({ children, ...props }) => (
      <Heading as="h1" size="2xl" my={6} color="#1A202C" {...props}>
        {children}
      </Heading>
    ),
    h2: ({ children, ...props }) => (
      <Heading as="h2" size="xl" my={5} color="#1A202C" {...props}>
        {children}
      </Heading>
    ),
    h3: ({ children, ...props }) => (
      <Heading as="h3" size="lg" my={4} color="#1A202C" {...props}>
        {children}
      </Heading>
    ),
    h4: ({ children, ...props }) => (
      <Heading as="h4" size="md" my={3} color="#1A202C" {...props}>
        {children}
      </Heading>
    ),
    h5: ({ children, ...props }) => (
      <Heading as="h5" size="sm" my={3} color="#1A202C" {...props}>
        {children}
      </Heading>
    ),
    h6: ({ children, ...props }) => (
      <Heading as="h6" size="xs" my={2} color="#1A202C" {...props}>
        {children}
      </Heading>
    ),
    
    // Paragraphs
    p: ({ children, ...props }) => (
      <Text mb={4} lineHeight="1.7" color="#2D3748" fontSize="md" {...props}>
        {children}
      </Text>
    ),
    
    // Lists
    ul: ({ children, ...props }) => (
      <UnorderedList spacing={2} mb={4} pl={4} {...props}>
        {children}
      </UnorderedList>
    ),
    ol: ({ children, ...props }) => (
      <OrderedList spacing={2} mb={4} pl={4} {...props}>
        {children}
      </OrderedList>
    ),
    li: ({ children, ...props }) => (
      <ListItem color="#2D3748" {...props}>
        {children}
      </ListItem>
    ),
    
    // Links
    a: ({ href, children, ...props }) => {
      // Handle different types of URLs
      const isExternalLink = href && (href.startsWith('http') || href.startsWith('https'));
      
      return (
        <Link 
          href={href} 
          color="#3182CE" 
          textDecoration="underline"
          isExternal={isExternalLink}
          _hover={{ 
            color: "#2C5282",
            textDecoration: "underline" 
          }}
          _focus={{
            outline: "2px solid #3182CE",
            outlineOffset: "2px"
          }}
          fontWeight="medium"
          {...props}
        >
          {children || href}
        </Link>
      );
    },
    
    // Images
    img: ({ src, alt, ...props }) => (
      <Box mb={4} textAlign="center">
        <Image 
          src={src} 
          alt={alt} 
          maxW="100%" 
          borderRadius="md"
          boxShadow="sm"
          {...props}
        />
      </Box>
    ),
    
    // Code blocks
    code: ({ inline, className, children, ...props }) => {
      return !inline ? (
        <Box
          as="pre"
          bg="#F7FAFC"
          p={4}
          borderRadius="md"
          overflowX="auto"
          mb={4}
          border="1px solid #E2E8F0"
          {...props}
        >
          <Code
            className={className}
            fontSize="sm"
            bg="transparent"
            p={0}
          >
            {children}
          </Code>
        </Box>
      ) : (
        <Code 
          bg="#F7FAFC" 
          px={1} 
          py={0.5} 
          borderRadius="sm"
          fontSize="sm"
          color="#E53E3E"
          {...props}
        >
          {children}
        </Code>
      );
    },
    
    // Blockquotes
    blockquote: ({ children, ...props }) => (
      <Box
        borderLeft="4px solid #3182CE"
        pl={4}
        ml={4}
        py={2}
        bg="#F7FAFC"
        fontStyle="italic"
        color="#4A5568"
        mb={4}
        {...props}
      >
        {children}
      </Box>
    ),
    
    // Horizontal rules
    hr: (props) => (
      <Divider my={6} {...props} />
    ),
    
    // Tables
    table: ({ children, ...props }) => (
      <Box overflowX="auto" mb={4}>
        <Table variant="simple" {...props}>
          {children}
        </Table>
      </Box>
    ),
    thead: ({ children, ...props }) => (
      <Thead {...props}>
        {children}
      </Thead>
    ),
    tbody: ({ children, ...props }) => (
      <Tbody {...props}>
        {children}
      </Tbody>
    ),
    tr: ({ children, ...props }) => (
      <Tr {...props}>
        {children}
      </Tr>
    ),
    th: ({ children, ...props }) => (
      <Th {...props}>
        {children}
      </Th>
    ),
    td: ({ children, ...props }) => (
      <Td {...props}>
        {children}
      </Td>
    ),
    
    // Strong and emphasis
    strong: ({ children, ...props }) => (
      <Text as="strong" fontWeight="bold" {...props}>
        {children}
      </Text>
    ),
    em: ({ children, ...props }) => (
      <Text as="em" fontStyle="italic" {...props}>
        {children}
      </Text>
    ),
  };

  // If no content, return empty
  if (!content) {
    return null;
  }

  // Clean the content before rendering
  const processedContent = cleanContent(content);

  return (
    <Box 
      {...props}
      sx={{
        // Remove margin from last child to prevent trailing space
        '& > *:last-child': {
          marginBottom: '0 !important'
        },
        // Hide empty paragraphs
        '& p:empty, & p:has(br:only-child)': {
          display: 'none'
        }
      }}
    >
      <ReactMarkdown
        components={components}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
      >
        {processedContent}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer;
